import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, TrendingUp, Sparkles, RefreshCw, Wand2 } from "lucide-react";
import { sr } from "../../lib/sr";

const CH_COLORS = { Display: "sr-badge", Social: "sr-badge-green", Video: "sr-badge-amber", Radio: "sr-badge", Email: "sr-badge-gray", Search: "sr-badge" };

export default function TrendsPanel({ limit }) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [gen, setGen] = useState({}); // trend index -> {busy, concept}

  const load = (refresh = false) => {
    if (refresh) setRefreshing(true);
    sr.creativeTrends(refresh)
      .then(setData)
      .catch(() => setData({ trends: [] }))
      .finally(() => setRefreshing(false));
  };
  useEffect(() => { load(); }, []);

  const compete = async (t, i) => {
    setGen((g) => ({ ...g, [i]: { busy: true } }));
    try {
      const r = await sr.generateCreative({
        channel: t.channel || "Display",
        goal: `${t.title} — ${t.angle}`,
        offer: t.example_offer,
        count: 1,
      });
      const concept = (r.creatives || [])[0];
      setGen((g) => ({ ...g, [i]: { busy: false, concept } }));
      toast.success("AI drafted a competing creative");
    } catch {
      setGen((g) => ({ ...g, [i]: { busy: false } }));
      toast.error("Could not generate creative");
    }
  };

  if (!data) return <div className="sr-card sr-empty" style={{ marginTop: 16 }}><Loader2 className="sr-spin" /></div>;
  const trends = (data.trends || []).slice(0, limit || 99);

  return (
    <div style={{ marginTop: 16 }} data-testid="trends-panel">
      <div className="sr-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div className="sr-eyebrow" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <TrendingUp size={14} /> Trending in {data.category || "your category"}
            </div>
            <p className="sr-muted" style={{ fontSize: 13, marginTop: 6, maxWidth: 620 }}>{data.note}</p>
          </div>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => load(true)} disabled={refreshing} data-testid="trends-refresh">
            {refreshing ? <Loader2 size={14} className="sr-spin" /> : <><RefreshCw size={14} /> Refresh</>}
          </button>
        </div>

        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", marginTop: 14 }}>
          {trends.map((t, i) => (
            <div key={i} className="sr-card" style={{ background: "var(--blue-light)", border: "1px solid var(--border)", display: "flex", flexDirection: "column" }} data-testid={`trend-${i}`}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                <span className={`sr-badge ${CH_COLORS[t.channel] || "sr-badge"}`}>{t.channel}</span>
                <span className="sr-badge sr-badge-gray">{t.angle}</span>
              </div>
              <h4 style={{ color: "var(--navy)", fontSize: 16 }}>{t.title}</h4>
              <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>{t.why_trending}</p>
              <div style={{ marginTop: 10, fontSize: 13 }}>
                <div style={{ color: "var(--navy)", fontWeight: 600 }}>“{t.example_headline}”</div>
                {t.example_offer && <div className="sr-muted" style={{ marginTop: 2 }}>Offer: {t.example_offer}</div>}
              </div>
              {gen[i]?.concept ? (
                <div className="sr-card" style={{ marginTop: "auto", background: "#fff" }} data-testid={`trend-concept-${i}`}>
                  <span className="sr-badge sr-badge-green" style={{ marginBottom: 6 }}><Sparkles size={11} /> Your AI version</span>
                  <div style={{ color: "var(--navy)", fontWeight: 700, fontSize: 14 }}>{gen[i].concept.headline}</div>
                  <p className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>{gen[i].concept.body}</p>
                  <div style={{ marginTop: 6 }}><span className="sr-badge">{gen[i].concept.cta}</span></div>
                  <Link to="/app/creative" style={{ fontSize: 12, marginTop: 8, display: "inline-block" }}>Open in Creative →</Link>
                </div>
              ) : (
                <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: "auto", width: "100%", justifyContent: "center" }}
                  onClick={() => compete(t, i)} disabled={gen[i]?.busy} data-testid={`trend-compete-${i}`}>
                  {gen[i]?.busy ? <Loader2 size={14} className="sr-spin" /> : <><Wand2 size={14} /> Create a competing version</>}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
